import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrackingLink } from '@/lib/marketing';
import {
  MessageSquare,
  Rocket,
  ArrowRight,
  Shield,
  Zap,
  GraduationCap,
  X,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
// Removed OptimizedImage - now using TwicPics for image optimization
import PageLayout from '@/components/askjds/PageLayout';
import { HomepageFlashcardDemo } from '@/components/home/HomepageFlashcardDemo';
import { HomepagePricingSection } from '@/components/home/HomepagePricingSection';
import { SectionDivider } from '@/components/home/SectionDivider';
import { NonServicesSection } from '@/components/home/NonServicesSection';
import { AboutSection } from '@/components/home/AboutSection';
import { useSEO, getOrganizationSchema, getWebsiteSchema, getFAQSchema } from '@/utils/seo';
import { PrelaunchBanner } from '@/components/home/PrelaunchBanner';

// Chat demo images
const chatDemos = [
  {
    id: 'concept-clarification',
    image: '/images/chat/chat_demo_1.png',
    title: 'Concept Clarification',
    description: 'Example: "Explain promissory estoppel like I\'m five."',
    altText: 'Screenshot of Ask JDS AI chat explaining promissory estoppel in simple, easy-to-understand language for law students'
  },
  {
    id: 'legal-distinctions',
    image: '/images/chat/chat_demo_2.png',
    title: 'Legal Distinctions',
    description: 'Example: "What\'s the difference between negligence and strict liability?"',
    altText: 'Ask JDS chat interface showing a clear comparison between negligence and strict liability legal concepts'
  },
  {
    id: 'case-analysis',
    image: '/images/chat/chat_demo_3.png',
    title: 'Case Analysis',
    description: 'Example: "Group project gone wrong. Hadley v. Baxendale—what\'s the damage?"',
    altText: 'Screenshot demonstrating Ask JDS analyzing Hadley v. Baxendale case law with practical applications for students'
  },
  {
    id: 'rapid-summaries',
    image: '/images/chat/chat_demo_4.png',
    title: 'Rapid Topic Summaries',
    description: 'Example: "For negligence, give me a one-paragraph high-yield summary of key rules."',
    altText: 'Ask JDS providing a concise, exam-focused summary of negligence law rules in the chat interface'
  }
];

export function HomePage() {
  const { user } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  // SEO Configuration
  useSEO({
    title: 'Law School & Bar Prep, Simplified | JD Simplified',
    description: 'JD Simplified helps law students and bar takers understand the law, practice applying it, and focus on what actually matters—without the overwhelm.',
    image: '/images/JD_Simplified_Hero.png',
    url: 'https://jdsimplified.com'
  });

  // Add structured data
  useEffect(() => {
    const addStructuredData = () => {
      // Remove any existing structured data scripts
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      // Add organization schema
      const orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.textContent = JSON.stringify(getOrganizationSchema());
      document.head.appendChild(orgScript);

      // Add website schema
      const webScript = document.createElement('script');
      webScript.type = 'application/ld+json';
      webScript.textContent = JSON.stringify(getWebsiteSchema());
      document.head.appendChild(webScript);

      // Add SoftwareApplication schema for law students + bar takers
      const appScript = document.createElement('script');
      appScript.type = 'application/ld+json';
      appScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "JD Simplified",
        "url": "https://www.jdsimplified.com",
        "applicationCategory": "EducationApplication",
        "operatingSystem": "Web",
        "isAccessibleForFree": true,
        "inLanguage": "en",
        "image": "https://www.jdsimplified.com/images/JD_Simplified_Hero.png",
        "description": "JD Simplified helps law students and bar takers understand the law, practice applying it, and focus on what actually matters—without the overwhelm.",
        "audience": [
          { "@type": "EducationalAudience", "audienceType": "Law students" },
          { "@type": "EducationalAudience", "audienceType": "Bar takers" }
        ],
        "offers": {
          "@type": "Offer",
          "price": "0.00",
          "priceCurrency": "USD"
        },
        "publisher": {
          "@type": "Organization",
          "name": "JD Simplified",
          "url": "https://www.jdsimplified.com"
        },
        "keywords": "AI law school app, law students, bar takers, law school notes, law school outlines, case briefs, flashcards, study tools"
      });
      document.head.appendChild(appScript);

      // Add FAQ schema
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(getFAQSchema());
      document.head.appendChild(faqScript);
    };

    addStructuredData();
  }, []);

  // Add PushLap Growth affiliate widget for authenticated users on homepage
  useEffect(() => {
    if (user?.email) {
      // Create and inject the widget script immediately
      const script = document.createElement('script');
      script.src = 'https://www.pushlapgrowth.com/affiliate-widget.js';
      script.setAttribute('data-affiliate-program-id', '4a22edb4-77a0-4c3a-8f7e-5901641176e9');
      script.setAttribute('data-user-email', user.email);
      script.setAttribute('data-affiliate-widget', 'true');

      // Use requestAnimationFrame to ensure DOM is ready without artificial delay
      requestAnimationFrame(() => {
        const widgetContainer = document.getElementById('homepage-affiliate-widget');
        if (widgetContainer && !widgetContainer.querySelector('script[data-affiliate-widget]')) {
          widgetContainer.appendChild(script);
        }
      });

      // Cleanup on unmount
      return () => {
        const existingScript = document.querySelector('#homepage-affiliate-widget script[data-affiliate-widget="true"]');
        if (existingScript && existingScript.parentNode) {
          existingScript.parentNode.removeChild(existingScript);
        }
      };
    }
  }, [user?.email]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenImage) {
          setFullscreenImage(null);
        } else if (isModalOpen) {
          closeModal();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen, fullscreenImage]);

  // Global keyboard navigation for carousel
  useEffect(() => {
    const handleGlobalKeyPress = (e: KeyboardEvent) => {
      // Only handle if no input/textarea is focused and modal is not open
      if (isModalOpen || document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyPress);
    return () => document.removeEventListener('keydown', handleGlobalKeyPress);
  }, [isModalOpen]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % chatDemos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + chatDemos.length) % chatDemos.length);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <PageLayout>
      <div
        className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950"
        style={{
          minHeight: '100vh',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Prelaunch Banner */}
        <PrelaunchBanner />

        {/* Hero Section */}
        <section id="top" className="pt-16 pb-16 md:pt-20 md:pb-24 relative overflow-hidden">
          <div className="absolute inset-0 animated-gradient opacity-60"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#F37022]/10 rounded-full filter blur-3xl animate-float-slow"></div>
            <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#00178E]/10 rounded-full filter blur-3xl animate-float-medium"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 box-border relative z-10">

            {/* Hero Content */}
            <div className="text-center mt-16">
              <h1 className="text-3xl md:text-5xl font-bold text-black dark:text-white mb-8">
                Your companion for mastering law school and the bar exam.
              </h1>

              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
                JD Simplified makes sense of the chaos so you can focus on mastering the law — not losing sleep over it.
              </p>

              <div className="flex flex-col md:flex-row gap-4 mt-8 justify-center">
                {user ? (
                  <TrackingLink
                    href="/chat"
                    campaign="homepage_hero"
                    content="start_chatting"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Start Chatting
                  </TrackingLink>
                ) : (
                  <TrackingLink
                    href="/auth?tab=signup"
                    campaign="homepage_hero"
                    content="start_free"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Rocket className="w-5 h-5" />
                    Start free today
                  </TrackingLink>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Problem / Promise Section */}
        <section id="problem" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 box-border text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-6">
              Law school makes it complicated. <span className="whitespace-nowrap">We make it clear.</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Outlines here, notes there, case briefs everywhere. JD Simplified puts it all in one intelligent platform—so you can stop surviving law school and start mastering it.
            </p>
            <Link
              to="#caselaw"
              className="inline-flex items-center gap-2 text-[#F37022] hover:text-[#E35D10] font-medium text-lg transition-colors"
            >
              See how it works
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Case Law Section - Text LEFT, Image RIGHT */}
        <section className="py-20 bg-blue-50 dark:bg-blue-950/30 overflow-x-hidden">
          <SectionDivider id="caselaw" label="Case Law" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - LEFT on desktop, FIRST on mobile */}
              <div className="order-1">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  Case briefs that actually brief you.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Clear, structured case summaries that highlight what matters—and why it matters on exams. Facts, rules, reasoning, and takeaways. Nothing extra. Everything essential.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  The cases you need. The clarity you deserve.
                </p>
                <TrackingLink
                  href="/cases"
                  campaign="homepage_features"
                  content="explore_cases"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Explore cases
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - RIGHT on desktop, SECOND on mobile */}
              <div className="order-2">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Case-Law.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Case-Law.png?twic=v1/resize=590/quality=85/output=auto&v=20260209"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Case-Law.png?twic=v1/resize=606/quality=85/output=auto&v=20260209 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Case-Law.png?twic=v1/resize=590/quality=85/output=auto&v=20260209 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Case-Law.png?twic=v1/resize=1180/quality=85/output=auto&v=20260209 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Case-Law.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="Structured case summary displaying facts, rule, reasoning, and takeaways with note link icons"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Study Section - Image LEFT, Text RIGHT */}
        <section className="py-20 bg-white dark:bg-gray-900 relative overflow-x-hidden">
          <SectionDivider id="study" label="Study" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 relative box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - FIRST on mobile, RIGHT on desktop */}
              <div className="order-1 md:order-2">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  Stop guessing what to study next.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Drill black-letter law, test your recall, and track what you've mastered. Hundreds of cards to start, with more added as you study.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  Review smarter. Remember longer.
                </p>
                <TrackingLink
                  href="/flashcards"
                  campaign="homepage_features"
                  content="enter_study_mode"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Enter Study Mode
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - SECOND on mobile, LEFT on desktop (using HomepageFlashcardDemo) */}
              <div className="order-2 md:order-1">
                <HomepageFlashcardDemo />
              </div>
            </div>
          </div>
        </section>

        {/* Issue Spotter Section - Text LEFT, Image RIGHT */}
        <section className="py-20 bg-white dark:bg-gray-900 overflow-x-hidden">
          <SectionDivider id="issue-spotter" label="Issue Spotter" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="order-1">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white">
                    What's the issue?
                  </h2>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                    New
                  </span>
                </div>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Practice spotting legal issues before exam day. Upload a fact pattern, identify what you see, and get clear feedback on what you caught—and what you missed.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  No pressure. Just practice that builds real exam instincts.
                </p>
                <TrackingLink
                  href="/issue-spotter"
                  campaign="homepage_features"
                  content="try_issue_spotter"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Try Issue Spotter
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>
              <div className="order-2">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Issue-Spotter.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Issue-Spotter.png?twic=v1/resize=590/quality=85/output=auto&v=20260209"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Issue-Spotter.png?twic=v1/resize=606/quality=85/output=auto&v=20260209 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Issue-Spotter.png?twic=v1/resize=590/quality=85/output=auto&v=20260209 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Issue-Spotter.png?twic=v1/resize=1180/quality=85/output=auto&v=20260209 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Issue-Spotter.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="Issue Spotter interface showing fact pattern analysis with identified legal issues"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Essay Coach Section - Image LEFT, Text RIGHT */}
        <section className="py-20 bg-orange-50 dark:bg-orange-950/30 overflow-x-hidden">
          <SectionDivider id="essay-coach" label="Essay Coach" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="order-1 md:order-2">
                <div className="mb-4">
                  <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white inline">
                    Feedback on your writing — the kind that&nbsp;helps.
                  </h2>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 ml-3 align-middle">
                    New
                  </span>
                </div>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  See exactly where points were earned or lost—issue coverage, rule accuracy, analysis depth. One clear breakdown. No guesswork.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  So you know what to fix next time — and why.
                </p>
                <TrackingLink
                  href="/essay-coach"
                  campaign="homepage_features"
                  content="try_essay_coach"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Try Essay Coach
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>
              <div className="order-2 md:order-1">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Essay-Coach.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Essay-Coach.png?twic=v1/resize=590/quality=85/output=auto&v=20260209"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Essay-Coach.png?twic=v1/resize=606/quality=85/output=auto&v=20260209 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Essay-Coach.png?twic=v1/resize=590/quality=85/output=auto&v=20260209 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Essay-Coach.png?twic=v1/resize=1180/quality=85/output=auto&v=20260209 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Essay-Coach.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="Essay Coach interface showing detailed feedback on legal writing with issue coverage, rule accuracy, and analysis depth"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Study Intelligence Section */}
        <section className="py-20 bg-blue-50 dark:bg-blue-950/30 overflow-x-hidden">
          <SectionDivider id="intelligence" label="Intelligence" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="order-1">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white">
                    Built to adapt as you practice.
                  </h2>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                    In Beta
                  </span>
                </div>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  As you practice with JD Simplified, the system adapts to how you're doing — not just what you're studying. If certain issues keep coming up, or if a rule is easy to recall but harder to apply, your practice and review shift to reflect that.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  Weak areas surface automatically — so each study session builds on the last.
                </p>
                <span className="inline-flex items-center gap-2 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium py-3 px-6 rounded-lg cursor-not-allowed">
                  In Beta
                </span>
              </div>
              <div className="order-2">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-blue-400/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Intelligence.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Intelligence.png?twic=v1/resize=590/quality=85/output=auto&v=20260209"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Intelligence.png?twic=v1/resize=606/quality=85/output=auto&v=20260209 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Intelligence.png?twic=v1/resize=590/quality=85/output=auto&v=20260209 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Intelligence.png?twic=v1/resize=1180/quality=85/output=auto&v=20260209 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Intelligence.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="Study Intelligence interface showing personalized learning insights and topic mastery"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notes Section - Image LEFT, Text RIGHT */}
        <section className="py-20 bg-white dark:bg-gray-900 overflow-x-hidden">
          <SectionDivider id="notes" label="Notes" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - FIRST on mobile, RIGHT on desktop */}
              <div className="order-1 md:order-2">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  Notes that keep up with you.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Take quick, focused notes with auto-save and instant search. Use legal-specific templates—case briefs, class notes, study outlines—or start from scratch. Organize with hashtags. Access from any device.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  Your notes. Your way. Always saved.
                </p>
                <TrackingLink
                  href="/notes"
                  campaign="homepage_features"
                  content="start_notes"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Start taking notes
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - SECOND on mobile, LEFT on desktop */}
              <div className="order-2 md:order-1">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Notes.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Notes.png?twic=v1/resize=590/quality=85/output=auto"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Notes.png?twic=v1/resize=606/quality=85/output=auto 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Notes.png?twic=v1/resize=590/quality=85/output=auto 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Notes.png?twic=v1/resize=1180/quality=85/output=auto 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Notes.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="JD Simplified note editor with tags, highlighted case links, and connected outline references"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Outlines Section - Text LEFT, Image RIGHT */}
        <section className="py-20 bg-orange-50 dark:bg-orange-950/30 overflow-x-hidden">
          <SectionDivider id="outlines" label="Outlines" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - LEFT on desktop, FIRST on mobile */}
              <div className="order-1">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  Outlines that do the heavy lifting.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Professional outlines for every core subject—full, cram, and bar-focused versions. Copy them to your notes with a single click to make them your own. Or upload your outlines and get instant AI feedback on accuracy, gaps, and what's missing.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  Start with structure. End with clarity.
                </p>
                <TrackingLink
                  href="/features/outlines"
                  campaign="homepage_features"
                  content="browse_outlines"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Browse outlines
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - RIGHT on desktop, SECOND on mobile */}
              <div className="order-2">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Outlines.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Outlines.png?twic=v1/resize=590/quality=85/output=auto&v=20260209"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Outlines.png?twic=v1/resize=606/quality=85/output=auto&v=20260209 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Outlines.png?twic=v1/resize=590/quality=85/output=auto&v=20260209 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Outlines.png?twic=v1/resize=1180/quality=85/output=auto&v=20260209 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Outlines.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="Dynamic law school outline view with topic hierarchy and drag-and-drop sections"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chat Section - Image LEFT, Text RIGHT */}
        <section className="py-20 bg-blue-50 dark:bg-blue-950/30 overflow-x-hidden">
          <SectionDivider id="chat" label="Chat" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - FIRST on mobile, RIGHT on desktop */}
              <div className="order-1 md:order-2">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  When the law stops making sense, just ask.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Your built-in AI companion breaks down complex doctrines, translates professor-speak into plain English, and helps you spot issues before the next cold call.
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  The more you use the platform, the better Ask JDS gets at surfacing your weak spots.
                </p>
                <TrackingLink
                  href="/chat"
                  campaign="homepage_features"
                  content="start_chat"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Start a chat
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - SECOND on mobile, LEFT on desktop */}
              <div className="order-2 md:order-1">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Chat.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Chat.png?twic=v1/resize=590/quality=85/output=auto"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Chat.png?twic=v1/resize=606/quality=85/output=auto 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Chat.png?twic=v1/resize=590/quality=85/output=auto 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Chat.png?twic=v1/resize=1180/quality=85/output=auto 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Chat.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="JD Simplified chat explaining a law doctrine in structured, easy-to-read format"
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Courses Section - Text LEFT, Image RIGHT */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800/50 overflow-x-hidden">
          <SectionDivider id="courses" label="Courses" className="mb-20" />
          <div className="max-w-7xl mx-auto px-4 box-border">
            {/* Two-column layout on desktop, stacked on mobile */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text - LEFT on desktop, FIRST on mobile */}
              <div className="order-1">
                <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                  Courses designed for how law students learn.
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">
                  Structured lessons that work alongside your notes, outlines, and Study Mode—so understanding grows with every session.
                </p>
                <ul className="list-disc list-inside text-lg text-gray-600 dark:text-gray-400 mb-4 space-y-1">
                  <li>Civil Procedure</li>
                  <li>Intestacy</li>
                  <li>Legal Writing</li>
                  <li>And more launching soon</li>
                </ul>
                <p className="text-base text-gray-500 dark:text-gray-400 mb-6 italic">
                  Built for law school. Not around it.
                </p>
                <TrackingLink
                  href="/courses"
                  campaign="homepage_features"
                  content="view_courses"
                  className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  View courses
                  <ArrowRight className="w-5 h-5" />
                </TrackingLink>
              </div>

              {/* Image - RIGHT on desktop, SECOND on mobile */}
              <div className="order-2">
                <div
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:border-[#F37022]/30 cursor-zoom-in transform hover:scale-[1.02]"
                  onClick={() => setFullscreenImage('media:images/screenshots/JDS-Courses.png')}
                >
                  <div className="relative bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src="https://jdsimplified.twic.pics/images/screenshots/JDS-Courses.png?twic=v1/resize=590/quality=85/output=auto"
                      srcSet="
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Courses.png?twic=v1/resize=606/quality=85/output=auto 606w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Courses.png?twic=v1/resize=590/quality=85/output=auto 590w,
                      https://jdsimplified.twic.pics/images/screenshots/JDS-Courses.png?twic=v1/resize=1180/quality=85/output=auto 1180w
                    "
                      sizes="(max-width: 768px) 606px, 590px"
                      data-twic-src="media:images/screenshots/JDS-Courses.png"
                      data-twic-transform="quality=85/output=auto/resize=1180"
                      loading="lazy"
                      alt="JD Simplified course library teaser showing lessons connected to notes, outlines, and Study Mode."
                      className="w-full h-auto screenshot-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payoff / Transformation Section */}
        <section id="payoff" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 box-border text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-6">
              From panic to perspective. <span className="whitespace-nowrap">From outlines to outcomes.</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              JD Simplified is a complete system that helps you think like a lawyer while staying sane like a human. Understand more, retain more, and finally feel ahead of your syllabus instead of buried under it.
            </p>
            <TrackingLink
              href="/auth?tab=signup"
              campaign="homepage_conversion"
              content="payoff_start_free"
              className="inline-flex items-center gap-2 bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              Start free today
              <ArrowRight className="w-5 h-5" />
            </TrackingLink>
          </div>
        </section>

        <NonServicesSection />

        {/* Pricing Section */}
        <section className="py-20 bg-white dark:bg-gray-900 overflow-x-hidden">
          <SectionDivider id="pricing" label="Pricing" className="mb-20" />
          <div className="max-w-4xl mx-auto text-center px-4 box-border">
            <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-8">Simple, transparent pricing</h2>
          </div>
          <div className="px-4 box-border">
            <HomepagePricingSection />
          </div>
        </section>

        <AboutSection />

        {/* Social Proof Section */}
        <section id="proof" className="py-20 bg-orange-50 dark:bg-orange-950/30 relative overflow-hidden box-border">
          <div className="max-w-6xl mx-auto px-4 relative box-border">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
                Proud to study by your side
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Join thousands of law students and bar takers who trust JD Simplified.
              </p>
            </div>

            {/* Trust Points */}
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                <Shield className="w-10 h-10 text-[#F37022] mb-3" />
                <h3 className="text-xl font-semibold text-[#00178E] dark:text-blue-400 mb-2">Private by design</h3>
                <p className="text-gray-600 dark:text-gray-400">Your notes, outlines, and study progress stay private. Always.</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                <GraduationCap className="w-10 h-10 text-[#F37022] mb-3" />
                <h3 className="text-xl font-semibold text-[#00178E] dark:text-blue-400 mb-2">Built by law grads</h3>
                <p className="text-gray-600 dark:text-gray-400">I've been in your shoes. I built what I wished I had.</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                <Zap className="w-10 h-10 text-[#F37022] mb-3" />
                <h3 className="text-xl font-semibold text-[#00178E] dark:text-blue-400 mb-2">Fast search</h3>
                <p className="text-gray-600 dark:text-gray-400">Find what you need in seconds, not hours.</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md">
                <TrendingUp className="w-10 h-10 text-[#F37022] mb-3" />
                <h3 className="text-xl font-semibold text-[#00178E] dark:text-blue-400 mb-2">Thousands of active students</h3>
                <p className="text-gray-600 dark:text-gray-400">Join a community of law students studying smarter.</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <TrackingLink
                href="/auth?tab=signup"
                campaign="homepage_conversion"
                content="social_proof_cta"
                className="inline-flex items-center gap-2 text-[#F37022] hover:text-[#E35D10] font-medium text-lg transition-colors"
              >
                See why students switch
                <ArrowRight className="w-5 h-5" />
              </TrackingLink>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800/50 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 box-border">
            <h2 className="text-3xl md:text-5xl font-bold text-[#00178E] dark:text-blue-400 mb-6">
              Master law school. Master yourself.
            </h2>
            <p className="text-2xl text-[#00178E] dark:text-blue-300 mb-10">
              Join thousands of law students and bar takers studying smarter with JD Simplified—the AI companion built for clarity, not chaos.
            </p>
            <div className="flex flex-col items-center gap-8">
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                {user ? (
                  <TrackingLink
                    href="/chat"
                    campaign="homepage_conversion"
                    content="final_cta"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-4 px-8 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-xl"
                  >
                    <MessageSquare className="w-6 h-6" />
                    Start Chatting
                  </TrackingLink>
                ) : (
                  <TrackingLink
                    href="/auth?tab=signup"
                    campaign="homepage_conversion"
                    content="final_cta"
                    className="bg-[#F37022] hover:bg-[#E35D10] text-white font-medium py-4 px-8 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-xl"
                  >
                    <Rocket className="w-6 h-6" />
                    Sign Up
                  </TrackingLink>
                )}
              </div>
            </div>
          </div>
        </section>


      </div>

      {/* Image Enlargement Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative w-full h-full max-w-[90vw] max-h-[99vh] flex items-center justify-center">
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 bg-black/70 text-white p-3 rounded-full hover:bg-black/90 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              data-twic-src={`media:${chatDemos[currentImageIndex].image.substring(1)}`}
              data-twic-transform="quality=95/output=auto"
              data-twic-eager
              alt={chatDemos[currentImageIndex].altText}
              className="max-w-full max-h-full object-contain rounded-lg screenshot-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Fullscreen Screenshot Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative w-full h-full max-w-[95vw] max-h-[95vh] flex items-center justify-center">
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm z-10"
              aria-label="Close fullscreen"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={fullscreenImage.startsWith('media:') ? undefined : fullscreenImage}
              data-twic-src={fullscreenImage.startsWith('media:') ? fullscreenImage : undefined}
              data-twic-transform={fullscreenImage.startsWith('media:') ? "quality=95/output=auto" : undefined}
              data-twic-eager={fullscreenImage.startsWith('media:') ? true : undefined}
              alt="Screenshot fullscreen view"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            />
          </div>
        </div>
      )}

    </PageLayout>
  );
}